import { formatDifference } from "../../features/lazertrader/uptime";
import { useEffect, useState } from "react";
import { StatusIndicator, Status } from "./StatusIndicator";
import { isEmergencyMode } from "./emergency";
import dayjs from "dayjs";

export type Uptime =
  | {
      started_at?: Date;
      last_updated_at?: Date;
      execution_mode?: string;
    }
  | undefined;

const UptimeIndicator = (props: { label: string; uptime: Uptime }) => {
  const { label, uptime } = props;

  // Check if the page recently became visible
  // if it did, the data might be outdated.
  const canUpdate = useDetectVisibilityChange();

  const status = getStatus(uptime?.last_updated_at, canUpdate);
  const isEmergency = isEmergencyMode(uptime);

  const uptimeText = formatDifference(uptime?.started_at);
  const tooltip = isEmergency ? (
    <>
      <strong>EMERGENCY MODE</strong>
      <br />
      No algos are running on this exchange &ndash; all of them are stopped.
      <br />
      Click to return it to normal mode.
      {uptimeText && (
        <>
          <br />
          <br />
          {uptimeText}
        </>
      )}
    </>
  ) : (
    uptimeText
  );

  return (
    <StatusIndicator
      label={label}
      status={status}
      tooltip={tooltip}
      emergency={isEmergency}
    />
  );
};
export { UptimeIndicator };

const getStatus = (
  timeSinceLastUpdate: Date | undefined,
  dataCouldBeStale: boolean
): Status => {
  if (!timeSinceLastUpdate) {
    if (!dataCouldBeStale) {
      return Status.Unknown;
    } else {
      return Status.Unhealthy;
    }
  }
  const secondsSinceLastUpdate = dayjs().diff(timeSinceLastUpdate, "seconds");
  if (secondsSinceLastUpdate < 30) {
    return Status.Healthy;
  } else if (!dataCouldBeStale) {
    return Status.Unknown;
  } else if (secondsSinceLastUpdate < 60) {
    return Status.Warning;
  } else {
    return Status.Unhealthy;
  }
};

const useDetectVisibilityChange = () => {
  const [recentlyMadeVisible, setRecentlyMadeVisible] = useState(
    document.visibilityState === "visible"
  );
  useEffect(() => {
    // Handle page visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Retain last known state until new check is done
        setRecentlyMadeVisible(false);
        setTimeout(() => {
          setRecentlyMadeVisible(true);
        }, 5000); // Delay to prevent flashing
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);
  return recentlyMadeVisible;
};
